/**
 * F-148 §3.1 reference display. A system reference renders in `<bdi dir="ltr">` with a
 * "System reference" badge and help text; a merchant reference renders as authored in `<bdi>`.
 * Merchant content never goes through `t()`.
 */
import { useId } from 'react';
import { useTranslation } from '@dukanti/shared';
import type { ReceivingReferenceView } from '@dukanti/shared/admin';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface ReferenceBadgeProps {
  readonly reference: ReceivingReferenceView;
  /** Show the help sentence under a system reference (default true). */
  readonly showHelp?: boolean;
  readonly className?: string;
}

export function ReferenceBadge({
  reference,
  showHelp = true,
  className,
}: ReferenceBadgeProps): React.JSX.Element {
  const { t } = useTranslation();
  const helpId = useId();
  const isSystem = reference.referenceOrigin === 'system';

  return (
    <div className={cn('flex flex-col gap-1 text-start', className)} data-testid="reference-badge">
      <div className="flex flex-wrap items-center gap-2">
        {isSystem ? (
          <bdi
            dir="ltr"
            className="text-foreground font-mono text-sm"
            data-testid="reference-display"
            aria-describedby={showHelp ? helpId : undefined}
          >
            {reference.systemReference}
          </bdi>
        ) : (
          <bdi className="text-foreground text-sm font-medium" data-testid="reference-display">
            {reference.displayReference}
          </bdi>
        )}
        {isSystem ? (
          <Badge variant="neutral" data-testid="reference-system-badge">
            {t('admin.receiving.reference.systemBadge')}
          </Badge>
        ) : null}
      </div>
      {isSystem && showHelp ? (
        <p id={helpId} className="text-muted-foreground text-xs">
          {t('admin.receiving.reference.systemHelp')}
        </p>
      ) : null}
    </div>
  );
}
